import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';

import { CLASS_SIZE_LIMIT, redeemClassCode } from '../../lib/classCode';
import { useSession } from '../../lib/session';
import Styles from './ClassCodeModal.module.css';

const MESSAGES = {
  invalid: 'Type in the code your teacher gave you.',
  'not-found': "That code doesn't match a class. Codes are case-sensitive, so check the capital letters.",
  full: `This class is full. Each code works for ${CLASS_SIZE_LIMIT} students, so ask your teacher for help.`,
  'already-premium': 'Your account already has Premium, so you don’t need a class code.',
  denied: 'We couldn’t save your seat. Tell your teacher, or email support@brassflows.com.',
  error: 'Something went wrong. Please try again.',
};

// onClose goes back to the plans, onDone closes everything
const ClassCodeModal = ({ onClose, onDone }) => {
  const navigate = useNavigate();
  const user = useSession((s) => s.user);
  const [code, setCode] = useState('');
  const [status, setStatus] = useState('idle'); // idle | working | done
  const [error, setError] = useState('');
  const [rejoined, setRejoined] = useState(false);

  const busy = status === 'working';
  const busyRef = useRef(false);
  busyRef.current = busy;

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === 'Escape' && !busyRef.current) onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setError('');
    setStatus('working');
    const result = await redeemClassCode(code);
    if (result.ok) {
      setRejoined(result.alreadyJoined);
      setStatus('done');
    } else {
      setStatus('idle');
      setError(MESSAGES[result.reason] || MESSAGES.error);
    }
  };

  const goToAccount = (path) => {
    onDone();
    navigate(path);
  };

  let body;
  if (status === 'done') {
    body = (
      <>
        <p className={Styles.eyebrow}>All set</p>
        <h2 id="class-title" className={Styles.title}>You&rsquo;re in!</h2>
        <p className={Styles.text}>
          {rejoined
            ? 'Welcome back. Premium is switched on again.'
            : 'Premium is unlocked on your account, and your seat in the class is saved.'}
        </p>
        <button type="button" className={Styles.primary} onClick={onDone}>
          Start practicing
        </button>
      </>
    );
  } else if (!user) {
    body = (
      <>
        <p className={Styles.eyebrow}>Class code</p>
        <h2 id="class-title" className={Styles.title}>Join your class</h2>
        <p className={Styles.text}>
          Class seats are saved to your account. Log in or make one, then come back and type your code.
        </p>
        <div className={Styles.actions}>
          <button type="button" className={Styles.primary} onClick={() => goToAccount('/signup')}>
            Create account
          </button>
          <button type="button" className={Styles.ghost} onClick={() => goToAccount('/login')}>
            Log in
          </button>
        </div>
      </>
    );
  } else {
    body = (
      <form onSubmit={submit}>
        <p className={Styles.eyebrow}>Class code</p>
        <h2 id="class-title" className={Styles.title}>Join your class</h2>
        <p className={Styles.text}>
          Type the code from your teacher. Each code works for up to {CLASS_SIZE_LIMIT} students.
        </p>
        <div className={Styles.field}>
          <label htmlFor="class-code">Class code</label>
          <input
            id="class-code"
            className={Styles.input}
            type="text"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            maxLength={40}
            autoFocus
            autoComplete="off"
            autoCapitalize="off"
            spellCheck={false}
            placeholder="e.g. A4Pn49"
            disabled={busy}
          />
        </div>
        {error && <p className={Styles.error}>{error}</p>}
        <div className={Styles.actions}>
          <button type="submit" className={Styles.primary} disabled={busy || !code.trim()}>
            {busy ? <span className={Styles.loader} /> : 'Join class'}
          </button>
          <button type="button" className={Styles.ghost} onClick={onClose} disabled={busy}>
            Back
          </button>
        </div>
      </form>
    );
  }

  return createPortal(
    <div className={Styles.overlay} onClick={() => !busy && onClose()}>
      <div
        className={Styles.modal}
        role="dialog"
        aria-modal="true"
        aria-labelledby="class-title"
        onClick={(e) => e.stopPropagation()}
      >
        <button type="button" className={Styles.close} onClick={onClose} disabled={busy} aria-label="Close">
          ×
        </button>
        {body}
      </div>
    </div>,
    document.body,
  );
};

export default ClassCodeModal;